import { useState, useRef, useEffect, useCallback } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { TioTileField } from '../../api/tomorrowIoTilesService';
import type { RadarTile } from './useDataFetching';
import { loadLiveMinutes, getStorageKey } from './nowcastingStorage';
import { LRUImageCache } from './radarPreloader';
import { mapTileProviderForObservationsState } from './tioMapProviderPolicy';
import type { EventWithCircuit } from '../../api/eventsService';

// ============================================================================
// Types
// ============================================================================

export interface CardPositions {
  pip: { x: number; y: number } | null;
  stations: { x: number; y: number } | null;
  stationsComparison: { x: number; y: number } | null;
  tioForecast: { x: number; y: number } | null;
  serviceInfo: { x: number; y: number } | null;
  rainForecast: { x: number; y: number } | null;
  legend: { x: number; y: number } | null;
  schedule: { x: number; y: number } | null;
  minutelyForecast: { x: number; y: number } | null;
}

export type MapTileProvider = 'radar' | 'tomorrow.io' | 'tomorrow.io-global' | 'radar-l2' | 'radar-l2-numeric';

export const isTioProvider = (p: MapTileProvider): boolean =>
  p === 'tomorrow.io' || p === 'tomorrow.io-global';

/** null = follow app theme */
export type MapBasemapThemeOverride = 'dark' | 'light' | null;

// ============================================================================
// Constants
// ============================================================================

const CARD_POSITIONS_STORAGE_KEY = 'fia-portal-nowcasting-card-positions';
const PIP_SIZE_STORAGE_KEY = 'fia-portal-nowcasting-pip-size';
const STATIONS_COMPARISON_SIZE_STORAGE_KEY = 'fia-portal-nowcasting-stations-comparison-size';
const SPLIT_POSITION_STORAGE_KEY = 'fia-portal-nowcasting-split-position';
const DEFAULT_PIP_SIZE = { width: 400, height: 300 };
const DEFAULT_STATIONS_COMPARISON_SIZE = { width: 520, height: 420 };
const DEFAULT_SPLIT_POSITION = 50; // 50% width for left map

const DEFAULT_CARD_POSITIONS: CardPositions = {
  pip: null,
  stations: null,
  stationsComparison: null,
  tioForecast: null,
  serviceInfo: null,
  rainForecast: null,
  legend: null,
  schedule: null,
  minutelyForecast: null,
};

// ============================================================================
// LocalStorage Helpers
// ============================================================================

function loadFromLocalStorage<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.warn(`Failed to load ${key} from localStorage:`, e);
  }
  return defaultValue;
}

function loadBooleanFromLocalStorage(key: string, defaultValue: boolean): boolean {
  if (typeof window === 'undefined') return defaultValue;
  const saved = localStorage.getItem(key);
  return saved === null ? defaultValue : saved === 'true';
}

function loadNumberFromLocalStorage(key: string, defaultValue: number): number {
  if (typeof window === 'undefined') return defaultValue;
  const saved = localStorage.getItem(key);
  return saved ? parseInt(saved, 10) : defaultValue;
}

// ============================================================================
// Hook
// ============================================================================

export interface UseNowcastingStateOptions {
  initialMapTileProvider?: MapTileProvider;
}

export function useNowcastingState(options: UseNowcastingStateOptions = {}) {
  const { initialMapTileProvider = 'radar-l2' } = options;

  // ===========================================================================
  // Tile State
  // ===========================================================================
  const [tiles, setTiles] = useState<RadarTile[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  // L2 nowcast tiles (from the latest base timestamp) - stored separately for extendedTiles
  const [radarL2NowcastTiles, setRadarL2NowcastTiles] = useState<RadarTile[]>([]);

  // ===========================================================================
  // Playback State
  // ===========================================================================
  const [isPlaying, setIsPlaying] = useState(false);
  const [fps, setFps] = useState(1);
  const [autoLoop, setAutoLoop] = useState(false);
  /** When not looping in live mode: dock to 'first', 'last', 'current', or 'now' tile on refresh */
  const [playbackDock, setPlaybackDock] = useState<'first' | 'current' | 'now' | 'last'>(() => {
    const saved = localStorage.getItem('nowcasting-playback-dock');
    return (saved === 'first' || saved === 'last' || saved === 'current' || saved === 'now') ? saved : 'now';
  });

  // ===========================================================================
  // Radar/Map Layer State
  // ===========================================================================
  const [radarOpacity, setRadarOpacity] = useState(0.7);
  const [mapStyle, setMapStyle] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const saved = localStorage.getItem('nowcasting-map-style');
    // Valid map style keys from useMapInitialization
    if (saved === 'satellite' || saved === 'terrain') return saved;
    return null;
  });
  const [mapBasemapThemeOverride, setMapBasemapThemeOverride] = useState<MapBasemapThemeOverride>(() => {
    if (typeof window === 'undefined') return null;
    const saved = localStorage.getItem('nowcasting-map-basemap-theme-override');
    if (saved === 'dark' || saved === 'light') return saved;
    return null;
  });
  const [styleVersion, setStyleVersion] = useState(0);
  const [showWindArrows, setShowWindArrows] = useState(() => {
    const saved = localStorage.getItem('nowcasting-show-wind-arrows');
    return saved === 'true';
  });
  const [showStormTracking, setShowStormTracking] = useState(() => {
    const saved = localStorage.getItem('nowcasting-show-storm-tracking');
    return saved === 'true';
  });
  const [showRainPath, setShowRainPath] = useState(() => {
    const saved = localStorage.getItem('nowcasting-show-rain-path');
    return saved === 'true';
  });

  // ===========================================================================
  // Tomorrow.io State
  // ===========================================================================
  const [mapTileProvider, setMapTileProvider] = useState<MapTileProvider>(initialMapTileProvider);
  const [tioTileField, setTioTileField] = useState<TioTileField>(() => {
    const saved = localStorage.getItem('nowcasting-tio-tile-field');
    return (saved === 'rainIntensity' || saved === 'precipitationIntensity') 
      ? saved 
      : 'precipitationIntensity';
  });
  const [tioTimestamps, setTioTimestamps] = useState<string[]>([]);
  const [tioCurrentIndex, setTioCurrentIndex] = useState(0);
  const [isTioPlaying, setIsTioPlaying] = useState(false);

  // ===========================================================================
  // Live Mode Time Range Settings
  // ===========================================================================
  const [liveStartMinutes, setLiveStartMinutes] = useState<number>(() => 
    loadLiveMinutes(initialMapTileProvider, 'start', -5)
  );
  const [liveEndMinutes, setLiveEndMinutes] = useState<number>(() => 
    loadLiveMinutes(initialMapTileProvider, 'end', 60)
  );
  // Radar only: 0 = current time (observations), 30 = nowcasting (+5..+29 min tiles). Used for default range end and live mode.
  const [liveEndMinutesForRadar, setLiveEndMinutesForRadar] = useState<number>(() => {
    const key = 'nowcasting-live-end-minutes-radar';
    if (typeof window === 'undefined') return 30;
    const saved = localStorage.getItem(key);
    if (saved === null) return 30;
    const n = parseInt(saved, 10);
    return n === 0 ? 0 : 30;
  });

  // ===========================================================================
  // Visibility Toggles
  // ===========================================================================
  const [isStationsVisible, setIsStationsVisible] = useState(() =>
    loadBooleanFromLocalStorage('nowcasting-stations-visible', true)
  );
  const [isStationsComparisonVisible, setIsStationsComparisonVisible] = useState(() =>
    loadBooleanFromLocalStorage('nowcasting-stations-comparison-visible', false)
  );
  const [isServiceInfoVisible, setIsServiceInfoVisible] = useState(true);
  const [isRainForecastVisible, setIsRainForecastVisible] = useState(() =>
    loadBooleanFromLocalStorage('nowcasting-rain-forecast-visible', true)
  );
  const [isTioForecastVisible, setIsTioForecastVisible] = useState(() =>
    loadBooleanFromLocalStorage('nowcasting-tio-forecast-visible', true)
  );
  const [isMinutelyForecastVisible, setIsMinutelyForecastVisible] = useState(() =>
    loadBooleanFromLocalStorage('nowcasting-minutely-forecast-visible', true)
  );
  const [isScheduleVisible, setIsScheduleVisible] = useState(false);
  const [isLegendVisible, setIsLegendVisible] = useState(() =>
    loadBooleanFromLocalStorage('nowcasting-legend-visible', false)
  );
  const [isMapSettingsVisible, setIsMapSettingsVisible] = useState(false);
  const [isPiPVisible, setIsPiPVisible] = useState(false);
  const [isCircleOverlayVisible, setIsCircleOverlayVisible] = useState(() => 
    loadBooleanFromLocalStorage('nowcasting-circle-overlay-visible', false)
  );
  const [showRadarBbox, setShowRadarBbox] = useState(() =>
    loadBooleanFromLocalStorage('nowcasting-radar-bbox-visible', false)
  );
  const [isSplitViewEnabled, setIsSplitViewEnabled] = useState(() =>
    loadBooleanFromLocalStorage('nowcasting-split-view-enabled', false)
  );
  const [splitPosition, setSplitPosition] = useState<number>(() => 
    loadNumberFromLocalStorage(SPLIT_POSITION_STORAGE_KEY, DEFAULT_SPLIT_POSITION)
  );

  // ===========================================================================
  // Radius Circles Settings
  // ===========================================================================
  const [showRadiusCircles, setShowRadiusCircles] = useState(() => 
    loadBooleanFromLocalStorage('nowcasting-radius-circles-visible', false)
  );
  const [radiusCircleCount, setRadiusCircleCount] = useState(() => 
    loadNumberFromLocalStorage('nowcasting-radius-circle-count', 3)
  );
  const [radiusCircleDistance, setRadiusCircleDistance] = useState(() => 
    loadNumberFromLocalStorage('nowcasting-radius-circle-distance', 5)
  );

  // ===========================================================================
  // Track Line Width
  // ===========================================================================
  const [trackLineWidth, setTrackLineWidth] = useState(() =>
    loadNumberFromLocalStorage('nowcasting-track-line-width', 3)
  );

  // ===========================================================================
  // Numeric Radar Settings
  // ===========================================================================
  const [numericNoiseFloor, setNumericNoiseFloor] = useState<number>(() => {
    if (typeof window === 'undefined') return 0;
    const saved = localStorage.getItem('nowcasting-numeric-noise-floor');
    return saved ? parseFloat(saved) : 0;
  });
  const [numericColormap, setNumericColormap] = useState<string>(() => {
    if (typeof window === 'undefined') return 'marshall-palmer';
    return localStorage.getItem('nowcasting-numeric-colormap') || 'marshall-palmer';
  });

  // ===========================================================================
  // Card Positions & Dragging
  // ===========================================================================
  const [cardPositions, setCardPositions] = useState<CardPositions>(() => {
    const loaded = loadFromLocalStorage<CardPositions>(CARD_POSITIONS_STORAGE_KEY, DEFAULT_CARD_POSITIONS);
    return { ...DEFAULT_CARD_POSITIONS, ...loaded };
  });
  const [draggingCard, setDraggingCard] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // ===========================================================================
  // PiP Size
  // ===========================================================================
  const [pipSize, setPipSize] = useState<{ width: number; height: number }>(() => 
    loadFromLocalStorage(PIP_SIZE_STORAGE_KEY, DEFAULT_PIP_SIZE)
  );
  const [stationsComparisonSize, setStationsComparisonSize] = useState<{ width: number; height: number }>(() =>
    loadFromLocalStorage(STATIONS_COMPARISON_SIZE_STORAGE_KEY, DEFAULT_STATIONS_COMPARISON_SIZE)
  );

  // ===========================================================================
  // Map Refs
  // ===========================================================================
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);

  // ===========================================================================
  // Split Map Refs
  // ===========================================================================
  const splitMapContainerRef = useRef<HTMLDivElement>(null);
  const splitMapRef = useRef<MapboxMap | null>(null);

  // ===========================================================================
  // Draggable Card Refs
  // ===========================================================================
  const stationsCardRef = useRef<HTMLDivElement>(null);
  const stationsComparisonCardRef = useRef<HTMLDivElement>(null);
  const serviceInfoCardRef = useRef<HTMLDivElement>(null);
  const rainForecastCardRef = useRef<HTMLDivElement>(null);
  const tioForecastCardRef = useRef<HTMLDivElement>(null);
  const minutelyForecastCardRef = useRef<HTMLDivElement>(null);
  const legendCardRef = useRef<HTMLDivElement>(null);
  const scheduleCardRef = useRef<HTMLDivElement>(null);
  const pipCardRef = useRef<HTMLDivElement>(null);

  // ===========================================================================
  // Radar Layer IDs & Refs
  // ===========================================================================
  const radarSourceId = 'radar-tiles';
  const radarLayerId = 'radar-layer';
  const radarSourceId2 = 'radar-tiles-2';
  const radarLayerId2 = 'radar-layer-2';
  const radarActiveLayerRef = useRef<'layer1' | 'layer2'>('layer1');
  const radarCrossfadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ===========================================================================
  // Numeric Radar Layer IDs & Refs
  // ===========================================================================
  const radarNumericSourceId = 'radar-numeric-tiles';
  const radarNumericLayerId = 'radar-numeric-layer';
  const radarNumericSourceId2 = 'radar-numeric-tiles-2';
  const radarNumericLayerId2 = 'radar-numeric-layer-2';
  const radarNumericActiveLayerRef = useRef<'layer1' | 'layer2'>('layer1');
  const radarNumericCrossfadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ===========================================================================
  // Tomorrow.io Layer IDs & Refs
  // ===========================================================================
  const tioSourceId = 'tio-tiles';
  const tioLayerId = 'tio-layer';
  const tioSourceId2 = 'tio-tiles-2';
  const tioLayerId2 = 'tio-layer-2';
  const tioActiveLayerRef = useRef<'layer1' | 'layer2'>('layer1');
  const previousTioTimestampsRef = useRef<string[]>([]);
  const previousDateRangeRef = useRef<{ start: Date; end: Date } | null>(null);
  const tioTilePreloadRef = useRef<Set<string>>(new Set());

  // ===========================================================================
  // Caching & Event Tracking Refs
  // ===========================================================================
  const imageCacheRef = useRef<LRUImageCache>(new LRUImageCache(150));
  const previousEventRef = useRef<string | null>(null);

  // ===========================================================================
  // LocalStorage Persistence Effects
  // ===========================================================================

  // Save card positions
  useEffect(() => {
    try {
      localStorage.setItem(CARD_POSITIONS_STORAGE_KEY, JSON.stringify(cardPositions));
    } catch (e) {
      console.warn('Failed to save card positions to localStorage:', e);
    }
  }, [cardPositions]);

  // Save PiP size
  useEffect(() => {
    try {
      localStorage.setItem(PIP_SIZE_STORAGE_KEY, JSON.stringify(pipSize));
    } catch (e) {
      console.warn('Failed to save pip size to localStorage:', e);
    }
  }, [pipSize]);

  // Save stations comparison panel size
  useEffect(() => {
    try {
      localStorage.setItem(STATIONS_COMPARISON_SIZE_STORAGE_KEY, JSON.stringify(stationsComparisonSize));
    } catch (e) {
      console.warn('Failed to save stations comparison size to localStorage:', e);
    }
  }, [stationsComparisonSize]);

  // Save split view enabled state
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-split-view-enabled', String(isSplitViewEnabled));
    } catch (e) {
      console.warn('Failed to save split view state to localStorage:', e);
    }
  }, [isSplitViewEnabled]);

  // Save wind arrows visibility
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-show-wind-arrows', String(showWindArrows));
    } catch (e) {
      console.warn('Failed to save wind arrows state to localStorage:', e);
    }
  }, [showWindArrows]);

  // Save storm tracking visibility
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-show-storm-tracking', String(showStormTracking));
    } catch (e) {
      console.warn('Failed to save storm tracking state to localStorage:', e);
    }
  }, [showStormTracking]);

  // Save rain path visibility
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-show-rain-path', String(showRainPath));
    } catch (e) {
      console.warn('Failed to save rain path state to localStorage:', e);
    }
  }, [showRainPath]);

  // Save map style (Terrain, Satellite, Default)
  useEffect(() => {
    try {
      if (mapStyle === null) {
        localStorage.removeItem('nowcasting-map-style');
      } else {
        localStorage.setItem('nowcasting-map-style', mapStyle);
      }
    } catch (e) {
      console.warn('Failed to save map style to localStorage:', e);
    }
  }, [mapStyle]);

  // Save map basemap light/dark override (null = use app theme)
  useEffect(() => {
    try {
      if (mapBasemapThemeOverride === null) {
        localStorage.removeItem('nowcasting-map-basemap-theme-override');
      } else {
        localStorage.setItem('nowcasting-map-basemap-theme-override', mapBasemapThemeOverride);
      }
    } catch (e) {
      console.warn('Failed to save map basemap theme override to localStorage:', e);
    }
  }, [mapBasemapThemeOverride]);

  // Save split position
  useEffect(() => {
    try {
      localStorage.setItem(SPLIT_POSITION_STORAGE_KEY, String(splitPosition));
    } catch (e) {
      console.warn('Failed to save split position to localStorage:', e);
    }
  }, [splitPosition]);

  // Save legend visibility
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-legend-visible', String(isLegendVisible));
    } catch (e) {
      console.warn('Failed to save legend visibility to localStorage:', e);
    }
  }, [isLegendVisible]);

  // Save stations panel visibility
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-stations-visible', String(isStationsVisible));
    } catch (e) {
      console.warn('Failed to save stations panel visibility to localStorage:', e);
    }
  }, [isStationsVisible]);

  // Save stations comparison panel visibility
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-stations-comparison-visible', String(isStationsComparisonVisible));
    } catch (e) {
      console.warn('Failed to save stations comparison panel visibility to localStorage:', e);
    }
  }, [isStationsComparisonVisible]);

  // Save t.io forecast panel visibility
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-tio-forecast-visible', String(isTioForecastVisible));
    } catch (e) {
      console.warn('Failed to save t.io forecast panel visibility to localStorage:', e);
    }
  }, [isTioForecastVisible]);

  // Save minutely forecast panel visibility
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-minutely-forecast-visible', String(isMinutelyForecastVisible));
    } catch (e) {
      console.warn('Failed to save minutely forecast panel visibility to localStorage:', e);
    }
  }, [isMinutelyForecastVisible]);

  // Save rain forecast panel visibility
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-rain-forecast-visible', String(isRainForecastVisible));
    } catch (e) {
      console.warn('Failed to save rain forecast panel visibility to localStorage:', e);
    }
  }, [isRainForecastVisible]);

  // Save radar bbox visibility
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-radar-bbox-visible', String(showRadarBbox));
    } catch (e) {
      console.warn('Failed to save radar bbox visibility to localStorage:', e);
    }
  }, [showRadarBbox]);

  // Load live minutes when source changes
  useEffect(() => {
    const newStartMinutes = loadLiveMinutes(mapTileProvider, 'start', -5);
    setLiveStartMinutes(newStartMinutes);
    
    if (isTioProvider(mapTileProvider)) {
      const newEndMinutes = loadLiveMinutes(mapTileProvider, 'end', 60);
      setLiveEndMinutes(newEndMinutes);
    }
  }, [mapTileProvider]);

  // Save live start minutes
  useEffect(() => {
    try {
      const key = getStorageKey(mapTileProvider, 'start');
      localStorage.setItem(key, liveStartMinutes.toString());
    } catch (error) {
      console.error('Error saving liveStartMinutes to localStorage:', error);
    }
  }, [liveStartMinutes, mapTileProvider]);

  // Save live end minutes (only for tomorrow.io providers)
  useEffect(() => {
    if (isTioProvider(mapTileProvider)) {
      try {
        const key = getStorageKey(mapTileProvider, 'end');
        localStorage.setItem(key, liveEndMinutes.toString());
      } catch (error) {
        console.error('Error saving liveEndMinutes to localStorage:', error);
      }
    }
  }, [liveEndMinutes, mapTileProvider]);

  // Save radar live end minutes (0 = current time, 30 = nowcast)
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-live-end-minutes-radar', String(liveEndMinutesForRadar));
    } catch (error) {
      console.error('Error saving liveEndMinutesForRadar to localStorage:', error);
    }
  }, [liveEndMinutesForRadar]);

  // Save playback dock preference for live mode without loop
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-playback-dock', playbackDock);
    } catch (error) {
      console.error('Error saving playbackDock to localStorage:', error);
    }
  }, [playbackDock]);

  // Save numeric noise floor
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-numeric-noise-floor', String(numericNoiseFloor));
    } catch (e) {
      console.warn('Failed to save numeric noise floor to localStorage:', e);
    }
  }, [numericNoiseFloor]);

  // Save numeric colormap
  useEffect(() => {
    try {
      localStorage.setItem('nowcasting-numeric-colormap', numericColormap);
    } catch (e) {
      console.warn('Failed to save numeric colormap to localStorage:', e);
    }
  }, [numericColormap]);

  // ===========================================================================
  // Derived State Helpers
  // ===========================================================================
  
  const isRainOnlyMode = tioTileField === 'rainIntensity';

  const toggleRainOnlyMode = useCallback(() => {
    const newField = isRainOnlyMode ? 'precipitationIntensity' : 'rainIntensity';
    setTioTileField(newField);
    localStorage.setItem('nowcasting-tio-tile-field', newField);
  }, [isRainOnlyMode]);

  // ===========================================================================
  // State Reset Functions
  // ===========================================================================

  /**
   * Reset all state to initial values - used when event changes
   */
  const resetState = useCallback((options: {
    observationsEnabled?: boolean;
    selectedEvent?: EventWithCircuit | null;
  } = {}) => {
    const { observationsEnabled = true, selectedEvent = null } = options;
    
    // Clear tile state
    setTiles([]);
    setCurrentIndex(0);
    setRadarL2NowcastTiles([]);
    
    // Reset playback
    setIsPlaying(false);
    setAutoLoop(false);
    
    // Reset Tomorrow.io state
    setTioTimestamps([]);
    setTioCurrentIndex(0);
    setIsTioPlaying(false);
    
    // Set provider based on observations enabled
    setMapTileProvider(mapTileProviderForObservationsState(selectedEvent, observationsEnabled));
    
    // Reset refs
    previousTioTimestampsRef.current = [];
    previousDateRangeRef.current = null;
    imageCacheRef.current.clear();
    tioActiveLayerRef.current = 'layer1';
    radarActiveLayerRef.current = 'layer1';
  }, []);

  /**
   * Update radius circles visibility with localStorage persistence
   */
  const updateRadiusCirclesVisible = useCallback((value: boolean) => {
    setShowRadiusCircles(value);
    localStorage.setItem('nowcasting-radius-circles-visible', String(value));
  }, []);

  /**
   * Update radius circle count with localStorage persistence
   */
  const updateRadiusCircleCount = useCallback((value: number) => {
    setRadiusCircleCount(value);
    localStorage.setItem('nowcasting-radius-circle-count', String(value));
  }, []);

  /**
   * Update radius circle distance with localStorage persistence
   */
  const updateRadiusCircleDistance = useCallback((value: number) => {
    setRadiusCircleDistance(value);
    localStorage.setItem('nowcasting-radius-circle-distance', String(value));
  }, []);

  const updateTrackLineWidth = useCallback((value: number) => {
    setTrackLineWidth(value);
    localStorage.setItem('nowcasting-track-line-width', String(value));
  }, []);

  const updateNumericNoiseFloor = useCallback((value: number) => {
    setNumericNoiseFloor(value);
  }, []);

  const updateNumericColormap = useCallback((value: string) => {
    setNumericColormap(value);
  }, []);

  /**
   * Reset card positions to default (all null)
   */
  const resetCardPositions = useCallback(() => {
    setCardPositions(DEFAULT_CARD_POSITIONS);
    // localStorage will be updated automatically by the useEffect that watches cardPositions
  }, []);

  // ===========================================================================
  // Return
  // ===========================================================================

  return {
    // Tile State
    tiles,
    setTiles,
    currentIndex,
    setCurrentIndex,
    radarL2NowcastTiles,
    setRadarL2NowcastTiles,

    // Playback State
    isPlaying,
    setIsPlaying,
    fps,
    setFps,
    autoLoop,
    setAutoLoop,
    playbackDock,
    setPlaybackDock,

    // Radar/Map Layer State
    radarOpacity,
    setRadarOpacity,
    mapStyle,
    setMapStyle,
    mapBasemapThemeOverride,
    setMapBasemapThemeOverride,
    styleVersion,
    setStyleVersion,
    showWindArrows,
    setShowWindArrows,
    showStormTracking,
    setShowStormTracking,
    showRainPath,
    setShowRainPath,

    // Tomorrow.io State
    mapTileProvider,
    setMapTileProvider,
    tioTileField,
    setTioTileField,
    tioTimestamps,
    setTioTimestamps,
    tioCurrentIndex,
    setTioCurrentIndex,
    isTioPlaying,
    setIsTioPlaying,
    isRainOnlyMode,
    toggleRainOnlyMode,

    // Live Mode Time Range
    liveStartMinutes,
    setLiveStartMinutes,
    liveEndMinutes,
    setLiveEndMinutes,
    liveEndMinutesForRadar,
    setLiveEndMinutesForRadar,

    // Visibility Toggles
    isStationsVisible,
    setIsStationsVisible,
    isStationsComparisonVisible,
    setIsStationsComparisonVisible,
    isServiceInfoVisible,
    setIsServiceInfoVisible,
    isRainForecastVisible,
    setIsRainForecastVisible,
    isTioForecastVisible,
    setIsTioForecastVisible,
    isMinutelyForecastVisible,
    setIsMinutelyForecastVisible,
    isScheduleVisible,
    setIsScheduleVisible,
    isLegendVisible,
    setIsLegendVisible,
    isMapSettingsVisible,
    setIsMapSettingsVisible,
    isPiPVisible,
    setIsPiPVisible,
    isCircleOverlayVisible,
    setIsCircleOverlayVisible,
    showRadarBbox,
    setShowRadarBbox,
    isSplitViewEnabled,
    setIsSplitViewEnabled,
    splitPosition,
    setSplitPosition,

    // Radius Circles Settings
    showRadiusCircles,
    setShowRadiusCircles: updateRadiusCirclesVisible,
    radiusCircleCount,
    setRadiusCircleCount: updateRadiusCircleCount,
    radiusCircleDistance,
    setRadiusCircleDistance: updateRadiusCircleDistance,

    // Track Line Width
    trackLineWidth,
    setTrackLineWidth: updateTrackLineWidth,

    // Numeric Radar Settings
    numericNoiseFloor,
    setNumericNoiseFloor: updateNumericNoiseFloor,
    numericColormap,
    setNumericColormap: updateNumericColormap,

    // Card Positions & Dragging
    cardPositions,
    setCardPositions,
    draggingCard,
    setDraggingCard,
    dragOffset,
    setDragOffset,

    // PiP Size
    pipSize,
    setPipSize,
    stationsComparisonSize,
    setStationsComparisonSize,

    // Map Refs
    mapContainerRef,
    mapRef,

    // Split Map Refs
    splitMapContainerRef,
    splitMapRef,

    // Draggable Card Refs
    stationsCardRef,
    stationsComparisonCardRef,
    serviceInfoCardRef,
    rainForecastCardRef,
    tioForecastCardRef,
    minutelyForecastCardRef,
    legendCardRef,
    scheduleCardRef,
    pipCardRef,

    // Radar Layer IDs & Refs
    radarSourceId,
    radarLayerId,
    radarSourceId2,
    radarLayerId2,
    radarActiveLayerRef,
    radarCrossfadeTimeoutRef,

    // Numeric Radar Layer IDs & Refs
    radarNumericSourceId,
    radarNumericLayerId,
    radarNumericSourceId2,
    radarNumericLayerId2,
    radarNumericActiveLayerRef,
    radarNumericCrossfadeTimeoutRef,

    // Tomorrow.io Layer IDs & Refs
    tioSourceId,
    tioLayerId,
    tioSourceId2,
    tioLayerId2,
    tioActiveLayerRef,
    previousTioTimestampsRef,
    previousDateRangeRef,
    tioTilePreloadRef,

    // Caching & Event Tracking Refs
    imageCacheRef,
    previousEventRef,

    // Reset Function
    resetState,
    resetCardPositions,

    // Storage Keys (for external use if needed)
    CARD_POSITIONS_STORAGE_KEY,
    PIP_SIZE_STORAGE_KEY,
  };
}

export type NowcastingState = ReturnType<typeof useNowcastingState>;
